import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { injectIntl } from 'react-intl';

import { bindAll } from '@bibliocommons/utils-react';
import { withRouter } from '@bibliocommons/utils-routing';
import { Layout, MainContent, Sidebar } from 'app/components/shared/DrawerLayout';
import { libraryShape, didNotFindOptionsShape, routerShape } from '@bibliocommons/bc-prop-types';

import {
  FILTER_PREFIX,
  FILTER_SEPARATOR,
  FACET_FIELDS_ORDER,
  FACET_FIELDS_ORDER_ONLINE_FIRST
} from 'app/constants/BibConstants';
import AvailabilityDetailOverlayContainer from 'app/components/shared/AvailabilityDetailOverlay/AvailabilityDetailOverlayContainer';
import SearchScopeFieldContainer from 'app/components/search/SearchScopeField/SearchScopeFieldContainer';
import { BackToLinkManager } from 'app/components/BackToLinkManager';
import Context from '@bibliocommons/context';
import updateSearchQuery from 'app/helpers/search/updateSearchQuery';
import FieldList from '../../deprecated/shared/Facets/FieldList';
import SearchResults from '../SearchResults';
import SearchTitle from '../SearchTitle';
import ShelfAvailabilityTitle from '../ShelfAvailabilityTitle';
import MobileSearchBox from '../MobileSearchBox';

export class Search extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  handleExpandField(value) {
    this.props.searchActions.expandField(value, this.props.router.query);
  }

  transformQueryForFieldList(query) {
    const { searchType, activeFilters, router } = this.props;
    return updateSearchQuery({
      currentQuery: router.query,
      activeFilters,
      ...query,
      searchType
    });
  }

  resultContentStatusClassName() {
    return this.props.resultPaneActive ? 'active' : 'inactive';
  }

  renderSearchBox() {
    const { orderedSearchTypes, locked, query, searchType, activeFilters } = this.props;

    return (
      this.context.mobileApp && (
        <MobileSearchBox
          locked={locked}
          searchTypes={orderedSearchTypes}
          query={query}
          searchType={searchType}
          activeFilters={activeFilters}
        />
      )
    );
  }

  renderSearchResults() {
    // values
    const {
      activeFilters,
      branches,
      currentView,
      drawer,
      isFetching,
      featuredContent,
      featuredContentBlockPosition,
      fields,
      groupSearchFeedbackActions,
      libraryShortName,
      locked,
      pagination,
      results,
      rssURL,
      searchType,
      singleBranch,
      sortBys,
      library,
      ...otherProps
    } = this.props;

    // actions
    const { drawerActions, listPresentationActions, searchActions, didNotFindOptions, chatWidgets } = this.props;

    // Temporary adjustment for the period of COVID-19 pandemic.
    // If "Titles I can access online" is enabled by default (determined by a feature toggle),
    // we need to move the CIRC filters above the STATUS filters
    // so that CIRC filters are more visible.
    const fieldsOrder =
      library && library.get('defaultSearchToOnlineBibs') ? FACET_FIELDS_ORDER_ONLINE_FIRST : FACET_FIELDS_ORDER;

    return (
      <Layout open={drawer.get('open')} containerClass="results_container">
        <BackToLinkManager pageId="search" />
        <Sidebar sidebarClass="results_fields" drawerActions={drawerActions}>
          <FieldList
            separator={FILTER_SEPARATOR}
            prefix={FILTER_PREFIX}
            fieldsOrder={fieldsOrder}
            fields={fields}
            drawerActions={drawerActions}
            activeFilters={activeFilters}
            singleBranch={singleBranch}
            renderSearchScope={() => <SearchScopeFieldContainer />}
            handleExpandField={this.handleExpandField}
            transformQuery={this.transformQueryForFieldList}
          />
        </Sidebar>
        <MainContent mainClass={`result-content ${this.resultContentStatusClassName()}`}>
          <SearchResults
            {...otherProps}
            activeFilters={activeFilters}
            branches={branches}
            currentUser={this.props.currentUser}
            currentView={currentView}
            isFetching={isFetching}
            featuredContent={featuredContent}
            featuredContentBlockPosition={featuredContentBlockPosition}
            groupSearchFeedbackActions={groupSearchFeedbackActions}
            libraryShortName={libraryShortName}
            listPresentationActions={listPresentationActions}
            library={library}
            locked={locked}
            pagination={pagination}
            results={results}
            rssURL={rssURL}
            searchActions={searchActions}
            searchType={searchType}
            singleBranch={singleBranch}
            sortBys={sortBys}
            didNotFindOptions={didNotFindOptions}
            chatWidgets={chatWidgets}
          />
        </MainContent>
      </Layout>
    );
  }

  renderSearchTitleHeader() {
    const {
      query,
      searchType,
      broadened,
      didYouMeanQuerySuggestion,
      showExternalSearchLink,
      drawerActions,
      broadenedSearchType,
      broadenedSearchCount,
      didYouMeanResultCount,
      titleKey,
      suppress,
      customEdit,
      rssURL,
      didNotFindOptions,
      currentUser,
      library,
      shelfName
    } = this.props;

    if (shelfName && currentUser) {
      return <ShelfAvailabilityTitle shelfName={shelfName} userId={currentUser.get('id')} />;
    }

    return (
      <SearchTitle
        library={library}
        broadened={broadened}
        broadenedSearchCount={broadenedSearchCount}
        broadenedSearchType={broadenedSearchType}
        customEdit={customEdit}
        didYouMeanQuerySuggestion={didYouMeanQuerySuggestion}
        didYouMeanResultCount={didYouMeanResultCount}
        drawerActions={drawerActions}
        id="searchTitle"
        query={query}
        searchType={searchType}
        showExternalSearchLink={showExternalSearchLink}
        suppress={suppress}
        titleKey={titleKey}
        rssURL={rssURL}
        didNotFindOptions={didNotFindOptions}
      />
    );
  }

  render() {
    return (
      <div>
        {this.renderSearchBox()}
        {this.renderSearchTitleHeader()}
        {this.renderSearchResults()}
        <AvailabilityDetailOverlayContainer />
      </div>
    );
  }
}

Search.defaultProps = {
  currentView: 'medium'
};

Search.propTypes = {
  activeFilters: ImmutablePropTypes.map.isRequired,
  branches: ImmutablePropTypes.list,
  broadened: PropTypes.bool,
  broadenedSearchCount: PropTypes.number,
  broadenedSearchType: PropTypes.string,
  catalogSearchOptions: ImmutablePropTypes.map,
  currentUser: ImmutablePropTypes.map,
  currentView: PropTypes.string,
  customEdit: PropTypes.bool,
  didYouMeanQuerySuggestion: PropTypes.string,
  didYouMeanResultCount: PropTypes.number,
  drawer: ImmutablePropTypes.map,
  drawerActions: PropTypes.objectOf(PropTypes.func),
  featuredContent: ImmutablePropTypes.map,
  featuredContentBlockPosition: PropTypes.number,
  fields: ImmutablePropTypes.list.isRequired,
  isFetching: PropTypes.bool,
  libraryShortName: PropTypes.string.isRequired,
  listPresentationActions: PropTypes.objectOf(PropTypes.func).isRequired,
  router: routerShape.isRequired,
  locked: PropTypes.bool,
  pagination: ImmutablePropTypes.map.isRequired,
  query: PropTypes.string.isRequired,
  resultPaneActive: PropTypes.bool,
  results: ImmutablePropTypes.list.isRequired,
  rssURL: PropTypes.string,
  searchActions: PropTypes.shape({
    betaSearch: PropTypes.func,
    clearFilters: PropTypes.func,
    closeExpandField: PropTypes.func,
    expandField: PropTypes.func
  }),
  searchType: PropTypes.string.isRequired,
  shelfName: PropTypes.string,
  showExternalSearchLink: PropTypes.bool,
  singleBranch: PropTypes.bool,
  sortBys: ImmutablePropTypes.list.isRequired,
  suppress: PropTypes.bool,
  titleKey: PropTypes.string,
  searchTypes: ImmutablePropTypes.list,
  didNotFindOptions: didNotFindOptionsShape,
  featuredCards: ImmutablePropTypes.list.isRequired,
  chatWidgets: ImmutablePropTypes.mapOf(PropTypes.object),
  library: libraryShape.isRequired,
  groupSearchFeedbackActions: PropTypes.objectOf(PropTypes.func),
  cardsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  orderedSearchTypes: ImmutablePropTypes.list
};

Search.contextType = Context;

export default withRouter(injectIntl(Search));
